import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSnapshot } from "valtio";
import { createUseStyles } from "react-jss";

import { fetchApi } from "../../../services/api";
import EventSelector from "../../EventSelector";
import { currentAccount } from "../../../proxies";

export const NewImageProvider = createContext(() => {});

export const useNewImageSelector = () => {
  const [image, setImage] = useState(null);
  const context = useContext(NewImageProvider);

  return [image, parent => context(m => setImage({ ...m })), setImage];
};

const useStyles = createUseStyles({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    "pointer-events": props => (props.show ? "auto" : "none"),
    "z-index": 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    "background-color": "rgba(0, 0, 0, 0.7)",
    "z-index": -1,
  },
  loading: {
    color: "white",
  },
  container: {
    width: "90%",
    "max-width": 700,
    "background-color": "#313131",
    "padding-bottom": 15,
    "border-radius": 5,
    "box-sizing": "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  images: {
    width: "100%",
    height: "50vh",
    overflow: "auto",
    "box-sizing": "border-box",
    "margin-top": 15,
    padding: [20, 0, 0, 20],
    position: "relative",
  },
  image: {
    width: "calc(50% - 20px)",
    height: 150,
    "border-radius": 10,
    "background-color": "#252525",
    "background-size": "cover",
    position: "relative",
    cursor: "pointer",
    float: "left",
    margin: [0, 20, 20, 0],

    "& p": {
      color: "white",
      margin: 0,
      position: "absolute",
      right: 10,
      bottom: 5,
      "text-shadow": "0 0 1px #000000, 0 0 1px #000000, 0 0 1px #000000",
      textAlign: "right",
    },
  },
  title: {
    color: "white",
    margin: 0,
    "font-size": 18,
  },
  message: {
    color: "white",
    margin: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const NewImageSelector = ({ show, onClose, onSubmit, tourInfo }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { event } = useSnapshot(currentAccount);
  const classes = useStyles({ show });

  useEffect(() => {
    if (event === null) {
      setImages([]);
      setLoading(false);
    } else {
      setLoading(true);

      fetchApi(`/images/list/${event}`).then(images => {
        setImages(
          images.map(item => ({
            id: item.id.toString(),
            name: item.title,
            width: item.width ?? null,
            height: item.height ?? null,
            fov: item.fov ?? null,
            x: 0,
            y: 0,
            urls: {
              full: item.url,
              low: item.url_low,
            },
            cube: item.cube ?? null,
            hotspots: [],
          })),
        );
        setLoading(false);
      });
    }
  }, [event]);

  return (
    <div className={classes.background}>
      <div className={classes.dim} onClick={onClose} />
      <div className={classes.container}>
        <h1>Image Selector</h1>
        {show && (
          <EventSelector
            className={classes.selector}
            tourInfo={tourInfo}
            light
          />
        )}
        <div className={classes.images}>
          {loading ? (
            <p className={classes.message}>Loading...</p>
          ) : images.length > 0 ? (
            images.map(image => (
              <div
                className={classes.image}
                key={image.id}
                style={{ backgroundImage: `url("${image.urls.low}")` }}
                onClick={() => onSubmit(image)}
              >
                <p>{image.name}</p>
              </div>
            ))
          ) : (
            <p className={classes.message}>No images found</p>
          )}
        </div>
      </div>
    </div>
  );
};

const NewImageSelectorProvider = ({ account, team, children }) => {
  const [show, setShow] = useState(false);
  const [imageCallback, setImageCallback] = useState(() => () => {});

  const selectImage = useCallback(callback => {
    setShow(true);
    setImageCallback(() => (...args) => {
      setShow(false);
      callback(...args);
    });
  }, []);

  return (
    <NewImageProvider.Provider value={selectImage}>
      <NewImageSelector
        show={show}
        onSubmit={imageCallback}
        onClose={() => setShow(false)}
        tourInfo={{ account, team }}
      />
      {children}
    </NewImageProvider.Provider>
  );
};

export default NewImageSelectorProvider;
